import React from "react";
import { useEffect, useState } from "react";
import { apiFundingShow } from "../lib/api";
import { Link, useParams } from "react-router-dom";

export default function FundingShow() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);

  useEffect(() => {
    (async () => {
      const res = await apiFundingShow(Number(id));
      setPayload(res);
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="section-box skeleton">Loading…</div>;

  const { holder, iban, bic, bankName, currency, method, funding, account, pdfUrl } = payload;

  return (
    <div className="max-w-4xl mx-auto card-box space-y-6">
      {/* Header & Actions */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600">Trust is</p>
          <h3 className="text-2xl font-semibold">{holder}</h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-white"
            onClick={() => navigator.clipboard.writeText(funding.reference)}
          >
            Copy REF
          </button>
          <a href={pdfUrl} className="btn">Download PDF</a>
        </div>
      </div>

      {/* Reference */}
      <div className="p-4 rounded border bg-gray-50">
        <p className="text-sm text-gray-600">Reference</p>
        <p className="text-2xl font-mono">{funding.reference}</p>
      </div>

      {/* SEPA / SWIFT cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="SEPA" rows={[
          ["Account Holder", holder || "—"],
          ["IBAN", iban || "—", true],
          ["Bank Name", bankName || "—"],
          ["Currency", currency],
          ["Reference", funding.reference, true],
        ]} />

        <Panel title="SWIFT" rows={[
          ["Account Holder", holder || "—"],
          ["Account Number / IBAN", iban || "—", true],
          ["SWIFT/BIC", bic || "—", true],
          ["Bank Name", bankName || "—"],
          ["Currency", currency],
          ["Reference", funding.reference, true],
        ]} />
      </div>

      <div className="p-4 rounded border bg-yellow-50">
        <p className="text-sm">
          <strong>Important:</strong> You must include the reference <span className="font-mono">{funding.reference}</span>
          {" "}in the payment message to ensure automatic matching. Funds appear as <em>Pending/Received</em> until compliance approval.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <Stat label="Requested amount" value={funding.amount ? `${Number(funding.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}` : "Variable"} />
        <Stat label="Method" value={`${method} — Account: ${account?.label ?? `#${account?.id}`}`} />
        <Stat label="Status" value={String(funding.status).replace(/^\w/, (c: string) => c.toUpperCase())} />
      </div>

      <div>
        <Link to="/funding" className="text-sm text-blue-600 hover:underline">Back to Funding Activity</Link>
      </div>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: [string, any, boolean?][] }) {
  return (
    <div className="p-4 rounded border bg-white">
      <h4 className="font-semibold mb-2">{title}</h4>
      <dl className="text-sm grid grid-cols-3 gap-y-2">
        {rows.map(([k, v, mono], i) => (
          <React.Fragment key={i}>
            <dt className="font-medium text-gray-600">{k}</dt>
            <dd className={`col-span-2 ${mono ? "font-mono" : ""}`}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded border bg-white">
      <p className="text-gray-600">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}
